/**
 * ChartBubble — inline chart artifact rendered in the workbench chat surface.
 *
 * Consumes a `WorkbenchChartSpec` produced by the LangGraph
 * `analytics_router_node` and emitted via the `chart_output` SSE event. The
 * same spec is rendered as a Prefab `PrefabApp` for external MCP clients —
 * see `app/chat/chart_spec.py:chart_spec_to_prefab`.
 *
 * Variants:
 *   <ChartBubble spec={...} />               — full-width (ChatShellPage)
 *   <ChartBubble spec={...} compact />        — constrained (EmbeddedChatPanel)
 */

import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import {
  ArrowRight,
  Pin,
  Download,
  ChevronDown,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import {
  creativeWorkbenchApi,
  type ChartCTA as ChartCTASpec,
  type ChartContext,
  type ChartType,
  type WorkbenchChartSpec,
} from '@/services/api';

// Workbench color palette — kept in sync with `WORKBENCH_CHART_COLORS` in
// `app/chat/chart_spec.py`.
const WORKBENCH_CHART_COLORS = [
  '#14b8a6', // teal-500
  '#1e3a5f', // navy-600
  '#8b5cf6', // violet-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#f43f5e', // rose-500
  '#94a3b8', // slate-400
] as const;

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface ChartBubbleProps {
  spec: WorkbenchChartSpec;
  narrative?: string;
  compact?: boolean;
  onSendMessage?: (text: string, opts?: { skillId?: string }) => void;
  threadId?: string;
}

export function ChartBubble({
  spec,
  narrative,
  compact = false,
  onSendMessage,
  threadId,
}: ChartBubbleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const handlePin = async () => {
    if (pinned || pinning) return;
    setPinning(true);
    try {
      await creativeWorkbenchApi.pinInsight({
        client_uid: spec.context.client_uid,
        persona_id: spec.context.persona_id,
        question: spec.title,
        chart_spec: spec,
        narrative,
        thread_id: threadId,
      });
      setPinned(true);
    } catch (err) {
      // Pin failures should not break the chat surface — surface in console only.
      // eslint-disable-next-line no-console
      console.warn('pin insight failed', err);
    } finally {
      setPinning(false);
    }
  };

  const handleExport = async (kind: 'png' | 'csv' | 'spec') => {
    setExportOpen(false);
    if (kind === 'png') {
      await downloadChartAsPng(containerRef.current, spec.title);
    } else if (kind === 'csv') {
      downloadDataAsCsv(spec);
    } else {
      void copySpecToClipboard(spec);
    }
  };

  return (
    <div
      ref={containerRef}
      className="rounded-xl border border-slate-200 overflow-hidden bg-white"
    >
      {narrative && (
        <div className="px-4 py-3 text-sm text-slate-700 bg-slate-50 border-b border-slate-100">
          {narrative}
        </div>
      )}

      <div className={compact ? 'p-3' : 'p-4'}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-navy-900 truncate">{spec.title}</div>
            {spec.subtitle && (
              <div className="text-[10px] text-slate-400 truncate">{spec.subtitle}</div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handlePin}
              disabled={pinned || pinning}
              className={
                pinned
                  ? 'text-[10px] text-teal-600 px-1.5 py-1 rounded flex items-center gap-1'
                  : 'text-[10px] text-slate-400 hover:text-teal-600 px-1.5 py-1 rounded flex items-center gap-1'
              }
              aria-label={pinned ? 'Pinned' : 'Pin chart'}
              title={pinned ? 'Pinned' : 'Pin to insights'}
            >
              <Pin size={11} />
              {pinned ? 'Pinned' : 'Pin'}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setExportOpen((v) => !v)}
                className="text-[10px] text-slate-400 hover:text-slate-700 px-1.5 py-1 rounded flex items-center gap-1"
                aria-label="Export chart"
                title="Export"
              >
                <Download size={11} />
                <ChevronDown size={9} />
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-slate-200 rounded-md shadow-md text-[11px] min-w-[140px]">
                  <button
                    type="button"
                    onClick={() => handleExport('png')}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
                  >
                    Download PNG
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
                  >
                    Download CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport('spec')}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
                  >
                    Copy chart spec
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {renderChart(spec, compact)}
      </div>

      {spec.cta && (
        <ChartCTABar
          cta={spec.cta}
          context={spec.context}
          onSendMessage={onSendMessage}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart routing
// ---------------------------------------------------------------------------

function renderChart(spec: WorkbenchChartSpec, compact: boolean) {
  const height = compact ? 180 : 260;
  switch (spec.chart_type as ChartType) {
    case 'area':
      return <AreaChartImpl spec={spec} height={height} />;
    case 'bar':
    case 'bar_grouped':
      return <BarChartImpl spec={spec} height={height} />;
    case 'bar_stacked':
      return <BarChartImpl spec={spec} height={height} stacked />;
    case 'multi_series':
      return <MultiSeriesChartImpl spec={spec} height={height} />;
    case 'scatter':
      return <ScatterChartImpl spec={spec} height={height} />;
    case 'table':
      return <TableImpl spec={spec} compact={compact} />;
    case 'progress':
      return <ProgressImpl spec={spec} />;
    case 'funnel':
      return <FunnelImpl spec={spec} />;
    default:
      return (
        <div className="text-xs text-slate-400">
          Unsupported chart_type: {String(spec.chart_type)}
        </div>
      );
  }
}

function colorFor(spec: WorkbenchChartSpec, index: number): string {
  const series = spec.series[index];
  if (series?.color) return series.color;
  return WORKBENCH_CHART_COLORS[index % WORKBENCH_CHART_COLORS.length];
}

// ---------------------------------------------------------------------------
// Chart implementations
// ---------------------------------------------------------------------------

interface ChartImplProps {
  spec: WorkbenchChartSpec;
  height: number;
  stacked?: boolean;
}

function AreaChartImpl({ spec, height }: ChartImplProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={spec.data}>
        <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
        <XAxis dataKey={spec.x_axis} tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} label={spec.y_axis_label ? { value: spec.y_axis_label, angle: -90, position: 'insideLeft', style: { fontSize: 10 } } : undefined} />
        <Tooltip />
        {spec.series.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
        {spec.series.map((s, i) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={colorFor(spec, i)}
            fill={colorFor(spec, i)}
            fillOpacity={0.18}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function BarChartImpl({ spec, height, stacked = false }: ChartImplProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={spec.data}>
        <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
        <XAxis dataKey={spec.x_axis} tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip />
        {spec.series.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
        {spec.series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={colorFor(spec, i)}
            stackId={stacked ? 'stack' : undefined}
            radius={[2, 2, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function MultiSeriesChartImpl({ spec, height }: ChartImplProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={spec.data}>
        <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
        <XAxis dataKey={spec.x_axis} tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {spec.series.map((s, i) =>
          i === 0 ? (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={colorFor(spec, i)} radius={[2, 2, 0, 0]} />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={colorFor(spec, i)}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          ),
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function ScatterChartImpl({ spec, height }: ChartImplProps) {
  const yKey = spec.series[0]?.key ?? 'y';
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart>
        <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
        <XAxis type="number" dataKey={spec.x_axis} name={spec.x_axis} tick={{ fontSize: 10 }} />
        <YAxis type="number" dataKey={yKey} name={spec.series[0]?.label ?? yKey} tick={{ fontSize: 10 }} />
        <ZAxis range={[40, 240]} />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
        <Scatter data={spec.data} fill={colorFor(spec, 0)} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function TableImpl({ spec, compact }: { spec: WorkbenchChartSpec; compact: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className={compact ? 'w-full text-[10px]' : 'w-full text-xs'}>
        <thead>
          <tr className="bg-slate-50 text-left text-slate-500 uppercase tracking-wide">
            {spec.series.map((s) => (
              <th key={s.key} className="px-2 py-1.5 font-semibold border-b border-slate-200">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.data.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
              {spec.series.map((s) => {
                const v = row[s.key];
                return (
                  <td key={s.key} className="px-2 py-1.5 text-slate-700">
                    {formatCell(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProgressImpl({ spec }: { spec: WorkbenchChartSpec }) {
  const row = spec.data[0] ?? {};
  const pct = clampPct(toNumber(row['pct']));
  const label = (row['label'] as string | undefined) ?? spec.series[0]?.label ?? '';
  const value = row['value'];
  const total = row['total'];
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-slate-700">
        <span>{label}</span>
        <span className="font-semibold text-navy-900">{pct.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full bg-teal-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {value !== undefined && total !== undefined && (
        <div className="text-[10px] text-slate-400">
          {formatCell(value)} of {formatCell(total)}
        </div>
      )}
    </div>
  );
}

function FunnelImpl({ spec }: { spec: WorkbenchChartSpec }) {
  const yKey = spec.series[0]?.key ?? 'value';
  const max = Math.max(...spec.data.map((row) => toNumber(row[yKey])), 1);
  return (
    <div className="space-y-1.5">
      {spec.data.map((row, i) => {
        const v = toNumber(row[yKey]);
        const pct = (v / max) * 100;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-24 truncate text-slate-600">{String(row[spec.x_axis])}</span>
            <div className="flex-1 bg-slate-100 rounded-md h-5 overflow-hidden">
              <div
                className="h-full bg-teal-500/80 flex items-center justify-end px-2 text-[10px] text-white"
                style={{ width: `${pct}%` }}
              >
                {v}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTA bar
// ---------------------------------------------------------------------------

interface ChartCTABarProps {
  cta: ChartCTASpec;
  context: ChartContext;
  onSendMessage?: (text: string, opts?: { skillId?: string }) => void;
}

function ChartCTABar({ cta, context, onSendMessage }: ChartCTABarProps) {
  const navigate = useNavigate();

  const handleCTA = () => {
    if (cta.skill_id === 'draft_brief') {
      const params = new URLSearchParams({
        skill: 'draft_brief',
        client: context.client_uid,
        persona: context.persona_id ?? '',
        prompt: cta.prompt,
      });
      navigate(`/chat/new?${params.toString()}`);
    } else if (cta.skill_id === 'suggest_briefs') {
      const params = new URLSearchParams();
      if (context.persona_id) params.set('persona', context.persona_id);
      navigate(`/creative/briefs/new?${params.toString()}`);
    } else if (onSendMessage) {
      onSendMessage(cta.prompt, { skillId: cta.skill_id });
    } else {
      const params = new URLSearchParams({ skill: cta.skill_id, prompt: cta.prompt });
      navigate(`/chat/new?${params.toString()}`);
    }
  };

  return (
    <div className="border-t border-slate-100 px-4 py-2 bg-slate-50 flex items-center justify-between">
      <span className="text-[10px] text-slate-400">Recommended next step</span>
      <button
        type="button"
        onClick={handleCTA}
        className="text-xs font-semibold text-teal-700 hover:text-teal-800 flex items-center gap-1"
      >
        {cta.label}
        <ArrowRight size={11} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function clampPct(v: number): number {
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
}

async function downloadChartAsPng(node: HTMLElement | null, title: string) {
  if (!node) return;
  // html2canvas is loaded lazily and treated as an optional peer dependency —
  // when not installed, PNG export silently degrades and CSV / Copy still work.
  try {
    // Use a dynamic import bypassed via Function() so TypeScript does not
    // require types to be present at compile time.
    type H2COptions = { backgroundColor?: string; scale?: number };
    const dynImport = new Function('m', 'return import(m)') as (
      m: string,
    ) => Promise<{
      default: (el: HTMLElement, opts?: H2COptions) => Promise<HTMLCanvasElement>;
    }>;
    const mod = await dynImport('html2canvas');
    const html2canvas = mod.default;
    if (!html2canvas) return;
    const canvas = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2 });
    const url = canvas.toDataURL('image/png');
    triggerDownload(url, `${slugify(title)}.png`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('PNG export failed (html2canvas unavailable):', err);
  }
}

function downloadDataAsCsv(spec: WorkbenchChartSpec) {
  const cols = spec.series.map((s) => s.key);
  const header = spec.series.map((s) => s.label).join(',');
  const lines = spec.data.map((row) =>
    cols.map((k) => csvEscape(row[k])).join(','),
  );
  const csv = [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${slugify(spec.title)}.csv`);
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

async function copySpecToClipboard(spec: WorkbenchChartSpec) {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return;
  try {
    await navigator.clipboard.writeText(JSON.stringify(spec, null, 2));
  } catch {
    // Clipboard refusals are silent — they only happen for non-interactive contexts.
  }
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'chart';
}

function triggerDownload(href: string, filename: string) {
  if (typeof document === 'undefined') return;
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
